import React from 'react'
import {
  consumeQueryParam,
  getUrlObject,
  parseUrl
} from '@jarvis.oddle/core/tools/routes'
import { combineString, isEmpty } from 'src/utils/string'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import loginFlow from 'src/flows/loginFlow'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import { parseJwt } from 'tools/jwtParser'
import { ODDLE_PASS_CLIENT_ID } from 'config/client'
import { verifyUrl } from 'tools/routes'
import useAppSelector from 'src/hooks/useAppSelector'
import {
  fetchUserAction,
  fetchUserFacebookAction,
  getAllAddressesOPAction,
  getAllSavedCardsAction,
  getOPAccessTokenAction,
  logoutAction,
  selectUser
} from 'src/redux/user'
import { selectDetailMenuSite, selectIsBot } from 'src/redux/meta'
import { selectSavedCreditCardAction } from 'src/redux/checkout'
import useAppDispatch from 'src/hooks/useAppDispatcher'

interface IUserContext {
  userInfo: UserStateData
  isUserLoggedIn: boolean
  isUserInfoFetched: boolean
  isOddlePassEnabled: boolean
  isNewOddlePassEnabled: boolean
  isLoginFailed: boolean
  isEnableOcaAddress: boolean
  isEnabledCustomerSaveCreditCardOnStripe: boolean
  login: (params?, options?) => Promise<void>
  fetchUserAction: (params) => Promise<{
    success: boolean
    payload?: {
      errorCode: string
      errorMessage: string
      payload:
        | {
            error?: string
          }
        | UserStateData
    }
  }>
  logout: (params) => Promise<Record<string, unknown>>
}

const UserContext = React.createContext<IUserContext>({
  userInfo: {},
  isUserLoggedIn: false,
  isUserInfoFetched: false,
  isOddlePassEnabled: false,
  isNewOddlePassEnabled: false,
  isLoginFailed: false,
  isEnableOcaAddress: false,
  isEnabledCustomerSaveCreditCardOnStripe: false,
  login: (params) => undefined,
  fetchUserAction: (params = {}) => undefined,
  logout: (params = {}) => undefined
} as IUserContext)

export const UserContextProvider = ({
  children
}: {
  children: React.ReactNode
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(selectUser)
  const meta = useAppSelector(selectDetailMenuSite)
  const isBot = useAppSelector(selectIsBot)
  const { t } = React.useContext(TranslationContext)
  const isUserInfoInitialised = React.useRef(false)
  const isFetchingUserInfo = React.useRef(false)
  const userInfo = user.data
  const isUserInfoFetched = user.status === 'fulfilled'
  const errorData = user.error

  const isUserLoggedIn = React.useMemo(() => {
    const { id, accountId } = user.data
    return isUserInfoFetched && !isEmpty(id) && !isEmpty(accountId)
  }, [isUserInfoFetched, user.data])

  const isLoginFailed = React.useMemo(() => !isEmpty(errorData), [errorData])

  const isOddlePassEnabled = meta?.oddlePassEnabled
  const isNewOddlePassEnabled = meta?.newOddlePassFlow
  const isEnableOcaAddress =
    meta?.enableOcaAddress && isOddlePassEnabled && isNewOddlePassEnabled
  const { enableCustomerSaveCreditCardOnStripe } = meta?.features || {}
  const isEnabledCustomerSaveCreditCardOnStripe =
    enableCustomerSaveCreditCardOnStripe &&
    isOddlePassEnabled &&
    isNewOddlePassEnabled

  const apiCallingAfterFetchingUser = React.useCallback(async () => {
    if (window.opw) {
      if (isOddlePassEnabled && isEnableOcaAddress) {
        await dispatch(getAllAddressesOPAction())
      }
      if (isEnabledCustomerSaveCreditCardOnStripe) {
        const savedCreditCards = await dispatch(getAllSavedCardsAction())
        if (savedCreditCards.meta.requestStatus === 'fulfilled') {
          const paymentMethods = savedCreditCards.payload.paymentMethods || []
          const defaultPmId = savedCreditCards.payload.defaultPmId || ''

          const creditCard =
            paymentMethods.find((card) => card.id === defaultPmId) ||
            paymentMethods[0]

          await dispatch(selectSavedCreditCardAction(creditCard))
        }
      }
    }
  }, [
    isOddlePassEnabled,
    isEnableOcaAddress,
    isEnabledCustomerSaveCreditCardOnStripe,
    dispatch
  ])

  const byPassAccessTokenFromEats = React.useCallback(
    async ({ clientId }) => {
      const { meta: metaFetchedUser, payload } = await dispatch(
        fetchUserAction({ clientId })
      )
      if (
        metaFetchedUser.requestStatus === 'fulfilled' &&
        isEmpty(payload.payload.error)
      ) {
        await apiCallingAfterFetchingUser()
        return { success: true, payload }
      }

      // when the customer logged in from eats and pass the accessToken if facing error request_new_access_token
      // will renew its accessToken.
      if (payload.payload.error === 'request_new_access_token') {
        const { customerId } = parseJwt(
          window.localStorage.getItem('accessToken')
        )
        const accessTokenResp = await dispatch(
          getOPAccessTokenAction({
            menuId: meta?.menuId,
            customerId
          })
        )
        if (
          accessTokenResp.meta.requestStatus === 'fulfilled' &&
          accessTokenResp.payload
        ) {
          const { accessToken } = accessTokenResp.payload.data
          window.localStorage.setItem('accessToken', accessToken)
          const { meta, payload } = await dispatch(
            fetchUserAction({ clientId: null })
          )
          if (
            meta.requestStatus === 'fulfilled' &&
            isEmpty(payload.payload.error)
          )
            await apiCallingAfterFetchingUser()
          return {
            success:
              meta.requestStatus === 'fulfilled' &&
              isEmpty(payload.payload.error),
            payload
          }
        }
      }

      return { success: true, payload }
    },
    [dispatch, meta, apiCallingAfterFetchingUser]
  )

  const wrapperFetchUserAction = React.useCallback(async () => {
    const { clientId } = parseJwt(
      window.localStorage.getItem('accessToken') || '{}'
    )
    // no need to send clientId, if its clientId is empty
    if (!isEmpty(clientId) && clientId !== ODDLE_PASS_CLIENT_ID) {
      return byPassAccessTokenFromEats({ clientId })
    }

    const { meta, payload } = await dispatch(
      fetchUserAction({ clientId: null })
    )
    const isLoggedIn =
      meta.requestStatus === 'fulfilled' && isEmpty(payload.payload.error)

    if (isLoggedIn) await apiCallingAfterFetchingUser()

    return { success: isLoggedIn, payload }
  }, [byPassAccessTokenFromEats, dispatch, apiCallingAfterFetchingUser])

  const triggerFacebookFlow = React.useCallback(
    async (isFacebookLoginSuccess) => {
      const hash = consumeQueryParam('hash')

      if (isFacebookLoginSuccess === 'true') {
        const response = await dispatch(fetchUserFacebookAction({ hash }))

        if (
          response.meta.requestStatus === 'fulfilled' &&
          response.payload.payload.facebookId
        ) {
          const {
            ocaExist,
            mcaExist,
            email,
            phone,
            firstName,
            lastName,
            facebookId
          } = response.payload.payload
          if (!ocaExist) {
            // No OCA
            loginFlow.start({
              type: 'signUp',
              data: {
                isFacebookSignUp: true,
                facebookId,
                hash,
                prefilledData: {
                  email,
                  phone,
                  firstName,
                  lastName,
                  password: '',
                  receiveEmail: true
                }
              }
            })
          } else if (!mcaExist) {
            // No MCA
            loginFlow.start({
              type: 'signUp',
              data: {
                ocaExist: true,
                facebookId,
                hash,
                email,
                prefilledData: {
                  phone,
                  firstName,
                  lastName,
                  receiveEmail: true
                }
              }
            })
          }
          // Have both MCA and OCA. Log user in.
          const userName = combineString(firstName, lastName)
          showNotificationToast.success(
            t('Welcome back _userName_', { userName })
          )
        } else {
          showNotificationToast.error(
            t('Oops! There seems to be a problem. Please try again later.')
          )
        }
      } else if (isFacebookLoginSuccess === 'false') {
        loginFlow.start()
      }

      pickUpDeliveryFlow.updateMeta({
        isVisited:
          window.localStorage.getItem('pickupDeliveryFlow.isVisited') === 'true'
      })
      window.localStorage.removeItem('pickupDeliveryFlow.isVisited')

      if (!isUserInfoFetched) {
        await wrapperFetchUserAction()
      }
    },
    [isUserInfoFetched, dispatch, t, wrapperFetchUserAction]
  )

  const triggerNormalFlow = React.useCallback(async () => {
    if (!isUserInfoFetched) {
      await wrapperFetchUserAction()
    }
  }, [wrapperFetchUserAction, isUserInfoFetched])

  React.useEffect(() => {
    if (isBot) return
    if (isFetchingUserInfo.current) return
    if (isUserInfoInitialised.current) return

    const urlObject = getUrlObject()
    const { path } = parseUrl({ url: urlObject.href })
    if (!verifyUrl({ url: urlObject.href, path })) return

    isFetchingUserInfo.current = true
    const fromFacebook = consumeQueryParam('fb-login-success')
    const handledByOddleShop =
      urlObject.search && urlObject.search.indexOf('viaOddlePass') < 0

    if (handledByOddleShop && fromFacebook) {
      triggerFacebookFlow(fromFacebook).then(() => {
        isUserInfoInitialised.current = true
        isFetchingUserInfo.current = false
      })
    } else {
      triggerNormalFlow().then(() => {
        isUserInfoInitialised.current = true
        isFetchingUserInfo.current = false
      })
    }
  }, [isBot, triggerFacebookFlow, triggerNormalFlow])

  const logout = React.useCallback(
    async ({ onSuccess, onFailure } = {}) => {
      if (isOddlePassEnabled && isNewOddlePassEnabled && window.opw) {
        const response = await window.opw.signOut(user.data.ocaId)
        if (!response.success || !response.success) {
          if (typeof onFailure === 'function') onFailure()
          return response
        }
      } else {
        const response = await dispatch(logoutAction())
        if (
          response.meta.requestStatus === 'fulfilled' ||
          response.payload.error === 'user_not_logged_in'
        ) {
          if (typeof onSuccess === 'function') onSuccess()
          showNotificationToast.success(t('Successfully logged out.'))

          // OMS back-end will do a data modification to the respective storeOrderId after the user has logged out.
          // to sync front-end data with back-end, front-end will re-initiate the storeOrder object by way of refreshing the page
          window.location.reload()
        } else {
          if (typeof onFailure === 'function') onFailure()
          return response
        }
      }
    },
    [isOddlePassEnabled, isNewOddlePassEnabled, user.data.ocaId, dispatch, t]
  )

  const loginWithNormalFlow = React.useCallback((actions) => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('accessToken')
    }
    if (actions && (actions.onFinished || actions.onExit)) {
      loginFlow.start(actions)
    } else {
      loginFlow.start()
    }
  }, [])

  const login = React.useCallback(
    async (actions = {}, options: Record<string, string>) => {
      if (isOddlePassEnabled && isNewOddlePassEnabled && window.opw) {
        try {
          const { success, data } = await window.opw.signIn(
            meta?.menuId,
            undefined,
            options
          )
          if (success) {
            if (actions.onFinished) {
              actions.onFinished({})
            }
          } else {
            if (actions.onExit) {
              actions.onExit()
            }
            if (data.action !== 'closeIframe') {
              showNotificationToast.error(
                t('Oops! There seems to be a problem. Please try again later.')
              )
              return loginWithNormalFlow(actions)
            }
          }
        } catch (e) {
          return loginWithNormalFlow(actions)
        }
      } else {
        return loginWithNormalFlow(actions)
      }
    },
    [
      isOddlePassEnabled,
      isNewOddlePassEnabled,
      loginWithNormalFlow,
      meta?.menuId,
      t
    ]
  )

  const userContext = React.useMemo(
    () => ({
      userInfo,
      isUserLoggedIn,
      isUserInfoFetched,
      isOddlePassEnabled,
      isNewOddlePassEnabled,
      logout,
      isLoginFailed,
      login,
      fetchUserAction: wrapperFetchUserAction,
      isEnableOcaAddress,
      isEnabledCustomerSaveCreditCardOnStripe
    }),
    [
      userInfo,
      isUserLoggedIn,
      isUserInfoFetched,
      isOddlePassEnabled,
      isNewOddlePassEnabled,
      logout,
      isLoginFailed,
      login,
      wrapperFetchUserAction,
      isEnableOcaAddress,
      isEnabledCustomerSaveCreditCardOnStripe
    ]
  )

  return (
    <UserContext.Provider value={userContext}>{children}</UserContext.Provider>
  )
}

export default UserContext
